import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Toaster } from "sonner";
import { initializePointerEventsMonitoring } from "@/lib/modal-utils";

// Layout Components
import AuthLayout from "./components/layout/auth-layout";
import MainLayout from "./components/layout/main-layout";

// Auth Pages
import LoginPage from "./pages/auth/login";

// Dashboard Pages
import DashboardHome from "./pages/dashboard/index";
import EmployeeDashboard from "./pages/dashboard/EmployeeDashboard";
import EmployeesPage from "./pages/dashboard/employees/index";
import DepartmentsPage from "./pages/dashboard/departments/index";
import AttendancePage from "./pages/dashboard/attendance/index";
import LeaveManagementPage from "./pages/dashboard/leave-management/index";
import PayrollPage from "./pages/dashboard/payroll/index";
import RecruitmentPage from "./pages/dashboard/recruitment/index";
import TrainingPage from "./pages/dashboard/training/index";
import PerformancePage from "./pages/dashboard/performance/index";
import MessagingPage from "./pages/messaging/MessagingPage";
import AnnouncementsPage from "./pages/dashboard/announcements/index";
import AnnouncementWithCommentsDemo from "./pages/AnnouncementWithCommentsDemo";
import SettingsPage from "./pages/dashboard/settings/index";

// Error Pages
import NotFoundPage from "./pages/errors/404";

// Hooks and Utils
import { AuthProvider, useAuth } from "./hooks/use-auth";
import { LoadingSpinner } from "./components/common/loading-spinner";
// import PerformancePage from "./pages/dashboard/performance";

function AppContent() {
  const { t } = useTranslation();
  const { user, loading, error } = useAuth();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize pointer events monitoring for modals
  useEffect(() => {
    const stopMonitoring = initializePointerEventsMonitoring();
    return stopMonitoring;
  }, []);

  // Initialize theme - Default to light mode
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");

    // Default to light mode unless explicitly set to dark
    if (savedTheme === "dark") {
      setIsDarkMode(true);
      document.documentElement.classList.add("dark");
    } else {
      setIsDarkMode(false);
      document.documentElement.classList.remove("dark");
      // Set light mode as default if no theme is saved
      if (!savedTheme) {
        localStorage.setItem("theme", "light");
      }
    }
  }, []);

  // Toggle theme
  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);

    if (newTheme) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  };

  // Show loading spinner during authentication check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    console.error("Authentication error:", error);
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            user ? (
              user.role.toUpperCase() === "EMPLOYE" ? (
                <Navigate to="/employee-dashboard" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            )
          }
        />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            user ? (
              user.role.toUpperCase() === "EMPLOYE" ? (
                <Navigate to="/employee-dashboard" replace />
              ) : (
                <Navigate to="/dashboard" replace />
              )
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Employee Dashboard Route */}
        <Route
          path="/employee-dashboard"
          element={
            user && user.role.toUpperCase() === "EMPLOYE" ? (
              <MainLayout
                user={user}
                isDarkMode={isDarkMode}
                toggleTheme={toggleTheme}
              >
                <EmployeeDashboard />
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        <Route
          path="/dashboard/*"
          element={
            user ? (
              <MainLayout
                user={user}
                isDarkMode={isDarkMode}
                toggleTheme={toggleTheme}
              >
                <Routes>
                  <Route
                    index
                    element={
                      user.role.toUpperCase() === "EMPLOYE" ? (
                        <Navigate to="/employee-dashboard" replace />
                      ) : (
                        <DashboardHome />
                      )
                    }
                  />
                  <Route
                    path="employees/*"
                    element={
                      user.role.toUpperCase() === "EMPLOYE" ? (
                        <Navigate to="/employee-dashboard" replace />
                      ) : (
                        <EmployeesPage />
                      )
                    }
                  />
                  <Route
                    path="departments/*"
                    element={
                      user.role.toUpperCase() === "EMPLOYE" ? (
                        <Navigate to="/employee-dashboard" replace />
                      ) : (
                        <DepartmentsPage />
                      )
                    }
                  />
                  <Route path="attendance/*" element={<AttendancePage />} />
                  <Route
                    path="leave-management/*"
                    element={<LeaveManagementPage />}
                  />
                  <Route path="payroll/*" element={<PayrollPage />} />
                  <Route
                    path="recruitment/*"
                    element={
                      user.role.toUpperCase() === "EMPLOYE" ? (
                        <Navigate to="/employee-dashboard" replace />
                      ) : (
                        <RecruitmentPage />
                      )
                    }
                  />
                  <Route path="training/*" element={<TrainingPage />} />
                  <Route path="messages/*" element={<MessagingPage />} />
                  <Route
                    path="performance/*"
                    element={
                      user.role.toUpperCase() === "EMPLOYE" ? (
                        <Navigate to="/employee-dashboard" replace />
                      ) : (
                        <PerformancePage />
                      )
                    }
                  />
                  <Route
                    path="announcements/*"
                    element={<AnnouncementsPage />}
                  />
                  <Route
                    path="demo-comments"
                    element={<AnnouncementWithCommentsDemo />}
                  />
                  <Route path="settings/*" element={<SettingsPage />} />
                </Routes>
              </MainLayout>
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* 404 Page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>

      {/* Sonner Toaster */}
      <Toaster position="top-right" richColors closeButton duration={4000} />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
